/**
 * Selection rules for the enrichment-run panel's attribute picker.
 *
 * The google agent (`GET google-agent/catalog/content-attributes`) publishes
 * `requires` on the wire, so dependency metadata is no longer a client concern. What
 * remains here is product policy the catalog does not describe: which groups are
 * gated, which carry a maturity badge, and which attributes cannot be switched off.
 */

/**
 * Group id → the attribute key the whole group depends on. A gated group with its
 * requirement unselected is locked: every attribute in it is forced off and its
 * header checkbox is inert.
 *
 * Search Attributes needs `description` because search descriptors and phrase
 * descriptions are generated *from* the enriched description — without it the steps
 * run against nothing and report a misconfiguration after the fact.
 *
 * Keyed by the agent's STABLE group id (`SEARCH`), not a slugged display name. It was
 * previously keyed `conversational`, which stopped matching when the group was renamed
 * to "Search Attributes" (agentic-config migration 0007) — so this map was silently
 * dead until the taxonomy moved to agent-services and gained stable ids.
 */
export const GROUP_REQUIRES: Readonly<Record<string, string>> = {
  SEARCH: 'description',
};

/**
 * Group id → badge token rendered beside the group label. The taxonomy has no `badge`
 * field, so maturity labelling is a client concern.
 *
 * Empty on purpose: the old `conversational: 'beta'` entry had been dead since
 * migration 0007 renamed the group, so production has shown Search Attributes
 * unbadged for months — and the design carries no badge. Re-keying it to `SEARCH`
 * during the agent-catalog migration accidentally resurrected it; design review
 * confirmed the badge should stay gone. The map (and its adapter/panel plumbing)
 * is kept so a future genuinely-beta group is a one-line entry.
 *
 * Values are i18n keys under `enrichmentRun.groupBadge`.
 */
export const GROUP_BADGES: Readonly<Record<string, string>> = {};

/**
 * Attribute keys that are always enriched and cannot be turned off in the run
 * panel. They render selected-but-inert (a lock affordance), survive Clear all,
 * and are always present in the payload. `google_product_category` was formerly
 * hidden and enriched implicitly; it is now shown as a mandatory attribute so the
 * operator sees everything a run will touch. Title and Description anchor every
 * run's generated copy, so they are mandatory too.
 */
export const MANDATORY_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  'title',
  'description',
  'google_product_category',
]);
